import { effect, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { DataRepositoryService } from '../data-repository/data-repository.service';
import { TaskInfoService } from '../task-info/task-info.service';
import { TBTabName } from '../../models/core';
import { TaskMovementService } from './task-movement.service';
import { SortEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { DataAccessService } from '../data-access/data-access.service';
import { ITasksMap, IPlannedPBMTasksMap } from '../../models/tasks-planned';
import { IUPPTasksMap } from '../../models/task-unplannedpickup';
import { IUnloadedTasksMap } from '../../models/task-unloaded';

/** Union of all task-bank record types used for sorting operations. */
type TaskRecord = ITasksMap | IUPPTasksMap | IPlannedPBMTasksMap | IUnloadedTasksMap;

@Injectable({
  providedIn: 'root'
})
export class SortingService implements OnDestroy{

  isCustomSortApplied: boolean = false; // this flag represents if customSort is applied at latest 
  isCustomSort: boolean = false; //this flag is used in HTML element attribute value

  //Signals and computation 
    
  //collects the sorted order of table with selected task first followed by non-selected task
  $sortedTasksForMovement: WritableSignal<any[]> = signal<any[]>([]);
  appliedColumnSort: SortEvent[] = [];

   //subscription variables
  sortServiceSubs$: Subscription[] = [];
  topSelectedTask: TaskRecord[] = [];

  constructor(
    private readonly dataRepositoryService: DataRepositoryService,
    private readonly taskInfoService: TaskInfoService,
    private readonly taskMovementService: TaskMovementService,
    private readonly dataAccessService: DataAccessService,
    
  ) { 

    //subscribe to tab switching observable
    this.sortServiceSubs$.push(
      this.dataAccessService.isTBTabSwitched$.subscribe(
        (isTabSwitched: boolean) => {
          if (isTabSwitched) {
            this.resetAppliedSorting();
          }
        }
      )
    );
    this.sortServiceSubs$.push(
      this.dataAccessService.isFavBLChanged$.subscribe(
        (isFavBLChanged: boolean) => {
          if (isFavBLChanged) {
            this.resetAppliedSorting();
          }
        }
      )
    );

    //effects methods
    this.taskSelectionResetEffect();
  }

  //lifecycle hooks
  ngOnDestroy(): void {
    this.sortServiceSubs$?.forEach((subs: Subscription) => {
      subs?.unsubscribe();
    });
  }

  //effect methods
  /**
   * Method runs the effect upon RESET action for the selected row 
   */
  taskSelectionResetEffect(){
    effect(() => {
      let selectedTaskCount = this.taskMovementService.taskmoveCount();
      if (!selectedTaskCount) {
        this.resetAppliedSorting();
      }
    });
  }

  /**
   * Method gets the dataset for selected tab and 
   * apply sorting the selected tasks (row selection or via Map) 
   * followed by non-selected task, and assigns to singal collection.
   */
  setSelectedTaskSorting() {
    //set the HTML element attribute value of customsort to TRUE
    if(!this.isCustomSortApplied){
      this.isCustomSort = true;
      this.applyCustomSorting();
    }
  }

  /**
   * Method set the custom sorting on the specific data set
   * and clubs the sorted and non-sorted dataset at last
   */
  applyCustomSorting(){
    this.$sortedTasksForMovement.set([]);
    this.taskMovementService.updateTaskSelectionPostDelta();

    const dataSet = this.taskInfoService.getSelectedTabValues();
    const objectIdentifier = this.taskInfoService.selectedTabName.toLowerCase() != TBTabName.Unloaded.toLowerCase() ? 'task_id' : 'item_id';

    //check if delta has updated the selected task entry (removing the task from the route)
    this.taskMovementService.selectedOptionsForTaskMove = this.taskMovementService.selectedOptionsForTaskMove.filter(
      (f) =>
        dataSet?.some((s: TaskRecord) => (f as object as Record<string, string | undefined>)[objectIdentifier] == Reflect.get(s, objectIdentifier))
    ).filter(x => x.isMovementAllowed);

    const selectedTask = this.taskMovementService.selectedOptionsForTaskMove.map(
      old => ({ ...old, ...dataSet.find((task: TaskRecord) => Reflect.get(task, objectIdentifier) === (old as object as Record<string, string | undefined>)[objectIdentifier]) }));
      
    let nonSelectedTask: TaskRecord[] = dataSet.filter(
      (task: TaskRecord) => !this.taskMovementService.selectedOptionsForTaskMove.some(updatedTask => (updatedTask as object as Record<string, string | undefined>)[objectIdentifier] === Reflect.get(task, objectIdentifier))
    );

    //re-apply the set sorting order to the dataset
    [...this.appliedColumnSort].reverse().forEach((sortOrder: SortEvent) => {
      nonSelectedTask = this.sortDataSet([...nonSelectedTask], sortOrder);
    });
    
    this.topSelectedTask = [...selectedTask];
    this.$sortedTasksForMovement.update(() => ([...selectedTask, ...nonSelectedTask]));

    if (selectedTask.length > 0) {
      this.isCustomSortApplied = true;
    } else {
      this.resetingCustomSorting();
    }
    
  }


  /**
   * Method resets the applied custom sorting on the Task bank tab
   * and applye the previously selected/default sorting
   */
  resetingCustomSorting(){
    this.$sortedTasksForMovement.set([]);
    this.isCustomSortApplied = false;
    this.appliedColumnSort = [];
     this.topSelectedTask = [];
    this.isCustomSort = false;
  }

  /**
   * Method resets the property of the services to default
   */
  resetAppliedSorting(){
    this.appliedColumnSort = [];
    this.topSelectedTask = [];
    this.isCustomSortApplied = false;
    this.isCustomSort = false;
  }

  /**
   * Method performs the sorting on input dataset, as per the sorting order
   * @param dataSet data colletion
   * @param sortOrder applied sorting order of sortEvent type
   * @returns sorted data set as per the sorting order
   */
  sortDataSet(dataSet: TaskRecord[], sortOrder: SortEvent): TaskRecord[] {
    dataSet.sort((d1: TaskRecord, d2: TaskRecord) => {
      const value1 = Reflect.get(d1, sortOrder.field!);
      const value2 = Reflect.get(d2, sortOrder.field!);
      let result = null;

      if (value1 == null && value2 != null) result = -1;
      else if (value1 != null && value2 == null) result = 1;
      else if (value1 == null && value2 == null) result = 0;
      else if (typeof value1 === "string" && typeof value2 === "string")
        result = value1.localeCompare(value2);
      // else result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
      else if (value1 < value2) result = -1
      else if (value1 > value2) result = 1
      else result = 0;

      return sortOrder.order! * result;
    });
    return dataSet;
  }

  /**
   * Method checks the event is new then sets the custom sort flag 
   * and set active applied column sorting 
   * @param sortEvent applied sorting event generated on column header click
   */
  setFlagsOnSortingEvent(sortEvent: SortEvent & { multisortmeta?: SortEvent['multiSortMeta'] }){
    //set the HTML element attribute value of customsort to FALSE
    this.isCustomSort = false;

    //customApplied sort flag should be false upon the header click, check the condition 
    const sortMeta = sortEvent.multisortmeta ?? sortEvent.multiSortMeta;
    if (sortMeta) {
      if (
        sortMeta.length &&
        this.appliedColumnSort.length !== 0 &&
        this.appliedColumnSort[0] !== sortMeta[0]
      ) {
        this.isCustomSortApplied = false;
      }
      this.appliedColumnSort = sortMeta;
    }
  }

  /**
   * Method resets the flags to default
   */
  setFlagOnSelectAllEvent(){
    this.isCustomSort = false;
    this.isCustomSortApplied = false;
  }

   /**
   * Method creates a list of object with custom sort applied task placed first 
   * and non-selected later in the list
   * @returns combined list of object
   */
  processCustomSortingWithDelta() {
    let mainData = this.taskInfoService.getSelectedTabValues();
    const objectIdentifier = this.taskInfoService.selectedTabName.toLowerCase() != TBTabName.Unloaded.toLowerCase() ? 'task_id' : 'item_id';
    const topSelectedIds = new Set(this.topSelectedTask.map(item => Reflect.get(item, 'task_id')));

    //get the topTask from updated Data
    let topUpdatedTasks: (TaskRecord | undefined)[] = this.topSelectedTask.map((m: TaskRecord) => {
      return mainData.find((f: TaskRecord) => Reflect.get(f, objectIdentifier) == Reflect.get(m, objectIdentifier));
    })

    //get the leftover tasks from updated Data
    let leftoverUpdatedTasks = mainData.filter((f: TaskRecord) => {
      return !topSelectedIds.has(Reflect.get(f, 'task_id'));
    });

    [...this.appliedColumnSort].reverse().forEach((sortOrder: SortEvent) => {
      leftoverUpdatedTasks = this.sortDataSet([...leftoverUpdatedTasks], sortOrder);
    });

    return [...topUpdatedTasks, ...leftoverUpdatedTasks] as any[];
  }
}
